import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { useAuth } from "@/contexts/AuthContext";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import SignupTeacher from "@/pages/SignupTeacher";
import SignupStudent from "@/pages/SignupStudent";
import SignupParent from "@/pages/SignupParent";
import SignupInstitute from "@/pages/SignupInstitute";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ChangePassword from "@/pages/ChangePassword";
import LandingKeyword from "@/pages/LandingKeyword";
import PublicTeacherProfile from "@/pages/PublicTeacherProfile";
import GuestClass from "@/pages/GuestClass";
import PublicRoom from "@/pages/PublicRoom";

import DashboardLayout from "@/components/layout/DashboardLayout";
import { MobiProvider } from "@/lib/MobiContext";
import TeacherDashboard from "@/pages/TeacherDashboard";
import StudentsPage from "@/pages/StudentsPage";
import ClassesPage from "@/pages/ClassesPage";
import LiveClassroom from "@/pages/LiveClassroom";
import AIQuizMaker from "@/pages/AIQuizMaker";
import AILessonPlanner from "@/pages/AILessonPlanner";
import AIImageGen from "@/pages/AIImageGen";
import AIPaperChecker from "@/pages/AIPaperChecker";
import AIGameCreator from "@/pages/AIGameCreator";
import AITutorChat from "@/pages/AITutorChat";
import DemoGuide from "@/pages/DemoGuide";
import LMSPage from "@/pages/LMSPage";
import LibraryPage from "@/pages/LibraryPage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import BillingSuccess from "@/pages/BillingSuccess";
import SettingsPage from "@/pages/SettingsPage";
import RecordingsPage from "@/pages/RecordingsPage";
import HomeworkGenerator from "@/pages/HomeworkGenerator";
import ScreenshotQuiz from "@/pages/ScreenshotQuiz";
import UnifiedWhiteboard from "@/pages/UnifiedWhiteboard";
import BoardLauncher from "@/pages/BoardLauncher";
import AILibrary from "@/pages/AILibrary";
import Curriculum from "@/pages/Curriculum";
import SubjectDetail from "@/pages/SubjectDetail";
import Subjects from "@/pages/Subjects";
import SlideDeckView from "@/pages/SlideDeckView";
import Grades from "@/pages/Grades";
import BookShelfPage from "@/pages/BookShelf";
import LessonBundle from "@/pages/LessonBundle";
import SkillsPage from "@/pages/SkillsPage";
import StudentSkills from "@/pages/StudentSkills";
import AnalyticsPage from "@/pages/AnalyticsPage";

import StudentDashboard from "@/pages/StudentDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminUsage from "@/pages/AdminUsage";
import AdminPackages from "@/pages/AdminPackages";
import AdminToolRequests from "@/pages/AdminToolRequests";
import ParentDashboard from "@/pages/ParentDashboard";
import ParentChildView from "@/pages/ParentChildView";
import InstituteDashboard from "@/pages/InstituteDashboard";
import RecordingShare from "@/pages/RecordingShare";
import StaffDashboard from "@/pages/StaffDashboard";
import SurveyRun from "@/pages/SurveyRun";
import AdminSurveys from "@/pages/AdminSurveys";
import AdminAccess from "@/pages/AdminAccess";
import AdminKpi from "@/pages/AdminKpi";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-neutral-500">Loading...</div>;
  if (!user) {
    // Preserve where the user tried to go so LoginPage can send them back
    // after they authenticate (fixes invite links → homepage).
    const next = `${loc.pathname}${loc.search || ""}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  if (user.password_change_required && loc.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }
  return children;
}

function App() {
  return (
    <MobiProvider>
      <div className="App">
        <Routes>
        {/* Public */}
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<SignupTeacher />} />
        <Route path="/signup/student" element={<SignupStudent />} />
        <Route path="/signup/parent" element={<SignupParent />} />
        <Route path="/signup/institute" element={<SignupInstitute />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/change-password" element={<ChangePassword />} />
        <Route path="/p/:slug" element={<PublicTeacherProfile />} />
        <Route path="/landing/:keyword" element={<LandingKeyword />} />
        <Route path="/guest/:classId" element={<GuestClass />} />
        <Route path="/room/:slug" element={<PublicRoom />} />
        <Route path="/rec/:token" element={<RecordingShare />} />

        {/* Teacher area */}
        <Route element={<Protected roles={["teacher", "admin"]}><DashboardLayout /></Protected>}>
          <Route path="/teacher" element={<TeacherDashboard />} />
          <Route path="/teacher/students" element={<StudentsPage />} />
          <Route path="/teacher/classes" element={<ClassesPage />} />
          <Route path="/teacher/quizzes" element={<AIQuizMaker />} />
          <Route path="/teacher/lesson-plans" element={<AILessonPlanner />} />
          <Route path="/teacher/ai-images" element={<AIImageGen />} />
          <Route path="/teacher/paper-checker" element={<AIPaperChecker />} />
          <Route path="/teacher/ai-games" element={<AIGameCreator />} />
          <Route path="/teacher/homework" element={<HomeworkGenerator />} />
          <Route path="/teacher/screenshot-quiz" element={<ScreenshotQuiz />} />
          <Route path="/teacher/whiteboard" element={<BoardLauncher />} />
          <Route path="/teacher/ai-library" element={<AILibrary />} />
          <Route path="/teacher/curriculum" element={<Curriculum />} />
          <Route path="/teacher/grades" element={<Grades />} />
          <Route path="/teacher/subjects" element={<Subjects />} />
          <Route path="/teacher/subjects/:id" element={<SubjectDetail />} />
          <Route path="/teacher/subjects/:subjectId/slides/:deckId" element={<SlideDeckView />} />
          <Route path="/teacher/ai-assistant" element={<AITutorChat persona="teacher_assistant" title="Teaching Assistant" />} />
          <Route path="/teacher/demo-guide" element={<DemoGuide />} />
          <Route path="/teacher/lms" element={<LMSPage />} />
          <Route path="/teacher/library" element={<LibraryPage />} />
          <Route path="/teacher/recordings" element={<RecordingsPage />} />
          <Route path="/teacher/billing" element={<SubscriptionPage />} />
          <Route path="/teacher/billing/success" element={<BillingSuccess />} />
          <Route path="/teacher/settings" element={<SettingsPage />} />
          <Route path="/teacher/bookshelf" element={<BookShelfPage />} />
          <Route path="/teacher/lesson-generator" element={<LessonBundle />} />
          <Route path="/teacher/skills" element={<SkillsPage />} />
          <Route path="/teacher/analytics" element={<AnalyticsPage />} />
          <Route path="/student/skills" element={<StudentSkills />} />
        </Route>

        {/* Live class — full-screen */}
        <Route path="/live/:classId" element={<Protected><LiveClassroom /></Protected>} />

        {/* Student area */}
        <Route element={<Protected roles={["student"]}><DashboardLayout /></Protected>}>
          <Route path="/student" element={<StudentDashboard />} />
          <Route path="/student/classes" element={<ClassesPage studentView />} />
          <Route path="/student/quizzes" element={<AIQuizMaker studentView />} />
          <Route path="/student/library" element={<LibraryPage />} />
          <Route path="/student/recordings" element={<RecordingsPage />} />
          <Route path="/student/ai-tutor" element={<AITutorChat persona="tutor" title="AI Tutor" />} />
          <Route path="/student/screenshot-quiz" element={<ScreenshotQuiz />} />
          <Route path="/student/settings" element={<SettingsPage />} />
          <Route path="/student/bookshelf" element={<BookShelfPage />} />
        </Route>

        {/* Parent area */}
        <Route element={<Protected roles={["parent"]}><DashboardLayout /></Protected>}>
          <Route path="/parent" element={<ParentDashboard />} />
          <Route path="/parent/child/:childId" element={<ParentChildView />} />
          <Route path="/parent/recordings" element={<RecordingsPage />} />
          <Route path="/parent/settings" element={<SettingsPage />} />
          <Route path="/parent/bookshelf" element={<BookShelfPage />} />
        </Route>

        {/* Institute area */}
        <Route element={<Protected roles={["institute"]}><DashboardLayout /></Protected>}>
          <Route path="/institute" element={<InstituteDashboard />} />
          <Route path="/institute/recordings" element={<RecordingsPage />} />
          <Route path="/institute/settings" element={<SettingsPage />} />
          <Route path="/institute/bookshelf" element={<BookShelfPage />} />
        </Route>

        {/* Admin */}
        <Route element={<Protected roles={["admin"]}><DashboardLayout /></Protected>}>
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/surveys" element={<AdminSurveys />} />
          <Route path="/admin/kpi" element={<AdminKpi />} />
          <Route path="/admin/access" element={<AdminAccess />} />
          <Route path="/admin/usage" element={<AdminUsage />} />
          <Route path="/admin/packages" element={<AdminPackages />} />
          <Route path="/admin/tool-requests" element={<AdminToolRequests />} />
        </Route>

        {/* Staff (call quality) + admin survey runner */}
        <Route element={<Protected roles={["staff", "admin"]}><DashboardLayout /></Protected>}>
          <Route path="/staff" element={<StaffDashboard />} />
          <Route path="/surveys/run/:sid" element={<SurveyRun />} />
        </Route>

        {/* Billing success generic */}
        <Route path="/billing/success" element={<Protected><BillingSuccess /></Protected>} />

        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </MobiProvider>
  );
}

export default App;
